/**
 * Formulario de vehículo, compartido por /fleet/new y /fleet/:id/edit.
 *
 * Uno solo y no dos: el alta tenía siete campos y el detalle mostraba trece.
 * Con formularios separados, cada campo nuevo habría que acordarse de añadirlo
 * en los dos sitios, y el que se olvidara quedaría invisible para siempre en
 * una de las dos pantallas.
 *
 * El componente no sabe si crea o actualiza: recibe los valores iniciales y
 * devuelve los del formulario. Quién los manda a la API lo decide la ruta.
 */
import { useState, type FormEvent } from 'react'
import { Button } from '@/components/ui/button'
import { Field } from '@/components/ui/field'

export const VEHICLE_TYPES = [
  'Car',
  'Van',
  'Truck',
  'Box Truck',
  'Forklift',
  'Trailer',
  'Other',
] as const

export const VEHICLE_STATUSES = [
  'Active',
  'In Repair',
  'Out of Service',
  'Sold',
  'Retired',
] as const

export const ODOMETER_UNITS = ['Miles', 'Kilometers', 'Hours'] as const

export interface VehicleFormValues {
  unitNumber: string
  vin: string | null
  make: string | null
  model: string | null
  year: number | null
  vehicleType: string
  color: string | null
  licensePlate: string | null
  status: string
  currentOdometer: number
  odometerUnit: string
  acquisitionPrice: number | null
  sunpassNumber: string | null
  notes: string | null
}

export interface VehicleFormProps {
  initial?: Partial<VehicleFormValues>
  submitLabel: string
  pending?: boolean
  error?: string | null
  /** Errores por campo que devuelve la API (ApiError.fields). */
  fieldErrors?: Record<string, string>
  onSubmit: (values: VehicleFormValues) => void
  /** Se pinta junto al botón de guardar: "Cancelar", "Eliminar"… */
  extraActions?: React.ReactNode
}

/** Los <input> son de texto; la API espera number|null. '' significa "sin dato". */
function toNumber(raw: string): number | null {
  const trimmed = raw.trim()
  if (!trimmed) return null
  const n = Number(trimmed)
  return Number.isFinite(n) ? n : null
}

export function VehicleForm({
  initial,
  submitLabel,
  pending = false,
  error,
  fieldErrors = {},
  onSubmit,
  extraActions,
}: VehicleFormProps) {
  const [unitNumber, setUnitNumber] = useState(initial?.unitNumber ?? '')
  const [vin, setVin] = useState(initial?.vin ?? '')
  const [make, setMake] = useState(initial?.make ?? '')
  const [model, setModel] = useState(initial?.model ?? '')
  const [year, setYear] = useState(initial?.year != null ? String(initial.year) : '')
  const [vehicleType, setVehicleType] = useState(initial?.vehicleType ?? 'Truck')
  const [color, setColor] = useState(initial?.color ?? '')
  const [licensePlate, setLicensePlate] = useState(initial?.licensePlate ?? '')
  const [status, setStatus] = useState(initial?.status ?? 'Active')
  const [currentOdometer, setCurrentOdometer] = useState(
    initial?.currentOdometer != null ? String(initial.currentOdometer) : '',
  )
  const [odometerUnit, setOdometerUnit] = useState(initial?.odometerUnit ?? 'Miles')
  const [acquisitionPrice, setAcquisitionPrice] = useState(
    initial?.acquisitionPrice != null ? String(initial.acquisitionPrice) : '',
  )
  const [sunpassNumber, setSunpassNumber] = useState(initial?.sunpassNumber ?? '')
  const [notes, setNotes] = useState(initial?.notes ?? '')

  function handleSubmit(e: FormEvent) {
    e.preventDefault()
    onSubmit({
      unitNumber: unitNumber.trim(),
      vin: vin.trim() || null,
      make: make.trim() || null,
      model: model.trim() || null,
      year: toNumber(year),
      vehicleType,
      color: color.trim() || null,
      licensePlate: licensePlate.trim() || null,
      status,
      currentOdometer: toNumber(currentOdometer) ?? 0,
      odometerUnit,
      acquisitionPrice: toNumber(acquisitionPrice),
      sunpassNumber: sunpassNumber.trim() || null,
      notes: notes.trim() || null,
    })
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-3">
      {error && (
        <p role="alert" className="text-sm font-medium text-danger">
          {error}
        </p>
      )}

      <Field
        label="Unit number"
        required
        value={unitNumber}
        onChange={(e) => setUnitNumber(e.target.value)}
        error={fieldErrors.unitNumber}
      />
      <Field
        label="VIN"
        value={vin}
        onChange={(e) => setVin(e.target.value)}
        error={fieldErrors.vin}
      />

      <div className="grid gap-3 sm:grid-cols-2">
        <Field label="Make" value={make} onChange={(e) => setMake(e.target.value)} />
        <Field label="Model" value={model} onChange={(e) => setModel(e.target.value)} />
        <Field
          label="Year"
          type="number"
          inputMode="numeric"
          value={year}
          onChange={(e) => setYear(e.target.value)}
          error={fieldErrors.year}
        />
        <Field label="Color" value={color} onChange={(e) => setColor(e.target.value)} />
      </div>

      <div className="grid gap-3 sm:grid-cols-2">
        <label className="flex flex-col gap-1.5 text-sm">
          <span className="font-medium">Type</span>
          <select
            className="h-11 rounded-lg border border-input bg-card px-3"
            value={vehicleType}
            onChange={(e) => setVehicleType(e.target.value)}
          >
            {VEHICLE_TYPES.map((t) => (
              <option key={t} value={t}>
                {t}
              </option>
            ))}
          </select>
        </label>

        <label className="flex flex-col gap-1.5 text-sm">
          <span className="font-medium">Status</span>
          <select
            className="h-11 rounded-lg border border-input bg-card px-3"
            value={status}
            onChange={(e) => setStatus(e.target.value)}
          >
            {VEHICLE_STATUSES.map((t) => (
              <option key={t} value={t}>
                {t}
              </option>
            ))}
          </select>
        </label>
      </div>

      <Field
        label="License plate"
        value={licensePlate}
        onChange={(e) => setLicensePlate(e.target.value)}
      />

      <div className="grid gap-3 sm:grid-cols-2">
        <Field
          label="Odómetro"
          type="number"
          inputMode="decimal"
          value={currentOdometer}
          onChange={(e) => setCurrentOdometer(e.target.value)}
          error={fieldErrors.currentOdometer}
        />
        <label className="flex flex-col gap-1.5 text-sm">
          <span className="font-medium">Unidad</span>
          <select
            className="h-11 rounded-lg border border-input bg-card px-3"
            value={odometerUnit}
            onChange={(e) => setOdometerUnit(e.target.value)}
          >
            {ODOMETER_UNITS.map((t) => (
              <option key={t} value={t}>
                {t}
              </option>
            ))}
          </select>
        </label>
      </div>

      <div className="grid gap-3 sm:grid-cols-2">
        <Field
          label="Precio de adquisición"
          type="number"
          inputMode="decimal"
          value={acquisitionPrice}
          onChange={(e) => setAcquisitionPrice(e.target.value)}
          error={fieldErrors.acquisitionPrice}
        />
        <Field
          label="SunPass"
          value={sunpassNumber}
          onChange={(e) => setSunpassNumber(e.target.value)}
        />
      </div>

      <label className="flex flex-col gap-1.5 text-sm">
        <span className="font-medium">Notas</span>
        <textarea
          rows={3}
          className="rounded-lg border border-input bg-card px-3 py-2 text-sm"
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
        />
      </label>

      <div className="flex flex-col gap-2 pt-1 sm:flex-row-reverse">
        <Button type="submit" className="sm:flex-1" loading={pending}>
          {submitLabel}
        </Button>
        {extraActions}
      </div>
    </form>
  )
}
